import React from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  PillTabs,
  PillTabsList,
  PillTabsTriggerHint,
  PillTabsTrigger,
  PillTabsContent,
} from "~/react/ui/pill-tabs"
import { TooltipProvider } from "~/react/ui/tooltip"
import {
  Filters,
  clearFilters,
  departmentsList,
  filterFunction,
  sortFunction,
  type FiltersType,
} from "~/react/components/search/filters"
import { CostCalculator } from "~/helpers/costCalculator"
import { createLeadList } from "~/react/components/lead-save-manager"
import {
  Result,
  ResultType,
} from "~/react/components/multi_domain_search/result"
import { type TabSelected } from "~/react/types/tabSelected"
import {
  leads_path,
  multi_domain_search_emails_search_index_path,
  multi_domain_search_reveal_search_index_path,
  api_lead_path,
  multi_domain_search_leads_search_index_path,
  mappings_locations_path,
} from "~/lib/routes"
import * as ActionCable from "@rails/actioncable"
import { useAsyncQueuer, batch } from "@tanstack/react-pacer"
import BulkActionsToolbar from "~/react/components/search/bulk-actions-toolbar"
import { Alert, AlertIcon, AlertContent } from "~/react/ui/alert"
import { DepartmentSection } from "~/react/components/search/department_section"
import { EmptyState } from "~/react/components/search/empty-state"
import { useCampaignsSave } from "~/react/hooks/useCampaignsSave"
import { EmailVerification } from "~/types/serializers/EmailVerification"
import { revealRequest } from "~/react/utils/emailSearchUtils"
import { useExportEmails } from "~/react/hooks/useExportEmails"
import { useJobTitleSuggestions } from "~/react/hooks/useJobTitleSuggestions"
import { useSplittedResults } from "../hooks/useSplittedResults"
import { useLocationMappings } from "../hooks/useLocationMappings"
import { LoadingSpinner } from "../ui/loading-spinner"

const estimatedSize = 80

interface DomainSidepanelEmailsProps {
  emailIds: number[]
  numberOfResults: number
  genericEmailsCount: number
  domain: {
    value: string
    company_name?: string
    company_data: boolean
  }
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  numberOfCreditsLeft: number
  singleCreditsBucket: boolean
}

type LocationsMappingsType = {
  continents: string[]
  business_regions: string[]
}

export default function DomainSidepanelEmails({
  emailIds: initialEmailIds,
  numberOfResults,
  genericEmailsCount,
  domain,
  numberOfCreditsLeft,
  singleCreditsBucket,
  leadsLists: initialLeadsLists,
  campaigns: initialCampaigns,
}: DomainSidepanelEmailsProps) {
  const [revealedIds, setRevealedIds] = React.useState<number[]>([])
  const [firstRevealAt, setFirstRevealAt] = React.useState<Date | null>(null)
  const [results, setResults] = React.useState<ResultType[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [reavealingIds, setRevealingIds] = React.useState<number[]>([])
  const [leadsLists, setLeadsLists] =
    React.useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [campaigns, setCampaigns] =
    React.useState<{ id: number; name: string }[]>(initialCampaigns)
  const [tabSelected, setTabSelected] =
    React.useState<TabSelected>("all_people")
  const locationsMappings = useLocationMappings()
  const [filters, setFilters] = React.useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    location: [],
  })
  const [selectedIds, setSelectedIds] = React.useState([])
  const [showFilters, setShowFilters] = React.useState(false)
  const [showFindByName, setShowFindByName] = React.useState(false)
  const exportEmails = useExportEmails(domain.value)

  // Loading hook for fetching emails
  const fetchEmailsFn = async (emailIds: number[]) => {
    const response = await fetch(
      multi_domain_search_emails_search_index_path(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
            ?.content,
        },
        body: JSON.stringify({ email_ids: emailIds }),
      },
    )
    const data = await response.json()

    setResults((prev) => {
      const existingIds = new Set(prev.map((result) => result.id))
      const newResults = data.emails.filter(
        (email) => !existingIds.has(email.id),
      )
      return [...prev, ...newResults]
    })
    if (queue.peekPendingItems().length === 0) {
      setIsLoading(false)
    }
  }
  const queue = useAsyncQueuer(fetchEmailsFn, { concurrency: 10, wait: 10 })

  React.useEffect(() => {
    setRevealingIds((prev) => prev.filter((id) => !revealedIds.includes(id)))
  }, [revealedIds])

  // Load initial emails
  React.useEffect(() => {
    if (initialEmailIds.length > 0) {
      setIsLoading(true)
      const batchSize = 5000

      for (let i = 0; i < initialEmailIds.length; i += batchSize) {
        queue.addItem(initialEmailIds.slice(i, i + batchSize))
      }
    }
  }, [initialEmailIds])

  const processResult = batch<ResultType>(
    (results) => {
      setResults((prev) => {
        const newRevealedIds: number[] = []
        const newResults = prev.map((result) => {
          const newResultData = results.find((r) => r.id === result.id)

          if (newResultData) {
            newRevealedIds.push(newResultData.id)
            return newResultData
          } else {
            return result
          }
        })
        setRevealedIds((prev) => [...new Set([...prev, ...newRevealedIds])])
        if (firstRevealAt === null) {
          setFirstRevealAt(new Date())
        }
        return newResults
      })
    },
    {
      maxSize: 10,
      wait: 250,
    },
  )

  // WebSocket connection for real-time updates
  React.useEffect(() => {
    const consumer = ActionCable.createConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: "MultiDomainSearchChannel" },
      {
        received: (
          data: { email: ResultType } | { verification: EmailVerification },
        ) => {
          if ("email" in data) {
            processResult(data.email)
          } else if ("verification" in data) {
            setResults((prev) =>
              prev.map((result) =>
                result.id === data.verification.email_id
                  ? { ...result, verification: data.verification }
                  : result,
              ),
            )
          }
        },
      },
    )

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleReveal = async (id: number | "generic") => {
    const result = results.find((result) => result.id === id)

    if (id === "generic" || (result && result.type === "generic")) {
      const result = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        {
          generic_email_domains: [domain.value],
        },
      )

      if (result === "lack_of_credits") {
        setRevealingIds([])
      } else if (id === "generic") {
        setRevealingGeneric(true)
      } else {
        setRevealingIds((prev) => [...prev, id])
      }
    } else {
      const result = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        {
          email_ids: [id],
        },
      )

      if (result === "lack_of_credits") {
        setRevealingIds([])
      } else {
        setRevealingIds((prev) => [...prev, id])
      }
    }
  }

  const handleLeadsListSave = async (
    name: string,
    listId: number,
    emailId: number,
  ) => {
    let list = { id: listId, name }
    if (listId === null) {
      // Create a new list
      list = await createLeadList(name)
      if (list) {
        setLeadsLists((prev) => [...prev, list])
      } else {
        return
      }
    }

    const lead = results.find((result) => result.id === emailId).lead

    if (!lead) {
      // Create a new lead
      const response = await fetch(
        multi_domain_search_leads_search_index_path(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
              ?.content,
          },
          body: JSON.stringify({
            email_id: emailId,
            leads_list_id: list.id,
          }),
        },
      )
      const json = await response.json()

      if (response.ok) {
        setResults((prev) =>
          prev.map((result) =>
            result.id === emailId ? { ...result, lead: json.data } : result,
          ),
        )
      } else {
        App.displayFlash("error", "Failed to save lead to a list")
      }
    } else {
      const response = await fetch(
        api_lead_path(lead.id, { api_version: "v2", api_key: gon.api_key }),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            leads_list_ids: [...lead.leads_list_ids, list.id],
          }),
        },
      )

      if (response.ok) {
        setResults((prev) =>
          prev.map((result) =>
            result.id === emailId
              ? {
                  ...result,
                  lead: {
                    ...result.lead,
                    leads_list_ids: [
                      ...new Set([...result.lead.leads_list_ids, list.id]),
                    ],
                  },
                }
              : result,
          ),
        )
      } else {
        App.displayFlash("error", "Failed to save lead to a list")
      }
    }
  }

  // Filter and sort results
  const filteredResults = React.useMemo(() => {
    const baseFilteredResults = filterFunction(
      results,
      filters,
      locationsMappings,
      revealedIds,
    )

    return baseFilteredResults.sort(sortFunction)
  }, [results, filters, revealedIds])

  const { allPeopleResults, decisionMakersResults, genericResults } =
    useSplittedResults(filteredResults)

  const handleSelect = (ids: number[]) => {
    let nextIds = [...selectedIds]

    ids.forEach((id) => {
      if (nextIds.includes(id)) {
        nextIds = nextIds.filter((item) => item !== id)
      } else {
        nextIds.push(id)
      }
    })
    setSelectedIds(nextIds)
  }

  const jobTitles = useJobTitleSuggestions(
    results,
    filters,
    locationsMappings,
    revealedIds,
  )

  // Group results by department for the "All People" tab
  const resultsByDepartment = React.useMemo(() => {
    const personalEmails = filteredResults.filter(
      (result) => result.type === "personal",
    )

    // Group by department
    const grouped = personalEmails.reduce(
      (acc, result) => {
        const dept = result.department || null
        if (!acc[dept]) acc[dept] = []
        acc[dept].push(result)
        return acc
      },
      {} as Record<string, ResultType[]>,
    )

    // Convert to array and sort by department name
    return Object.entries(grouped).sort(([deptA], [deptB]) => {
      if (deptA === null) return 1 // Unknown department goes last
      if (deptB === null) return -1
      return deptA.localeCompare(deptB)
    })
  }, [filteredResults])

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0

  // Bulk actions functions
  const saveLeads = async (listId: number | null) => {
    if (selectedIds.length > 0) {
      // Reveal only specific emails
      const selectedEmails = filteredResults.filter((result) =>
        selectedIds.includes(result.id),
      )
      const notGenericEmails = selectedEmails.filter(
        (result) => result.type !== "generic",
      )
      setRevealingIds((prev) => [
        ...prev,
        ...notGenericEmails
          .filter((result) => !result.revealed)
          .map((result) => result.id),
        ...selectedEmails.map((result) => result.id),
      ])
      const genericEmailsDomains = new Set(
        selectedEmails
          .filter((result) => result.type === "generic")
          .map((result) => result.domain),
      )

      if (genericEmailsDomains.size > 0) {
        // Reveal generic emails
        const result = await revealRequest(
          multi_domain_search_reveal_search_index_path(),
          "POST",
          {
            generic_email_domains: Array.from(genericEmailsDomains),
            leads_list_id: listId,
          },
        )

        if (result === "lack_of_credits") {
          setRevealingIds([])
        } else {
          setRevealingGeneric(true)
        }
      }
      if (notGenericEmails.length > 0) {
        // Reveal specific emails
        const result = await revealRequest(
          multi_domain_search_reveal_search_index_path(),
          "POST",
          {
            email_ids: notGenericEmails.map((result) => result.id),
            leads_list_id: listId,
          },
        )

        if (result === "lack_of_credits") {
          setRevealingIds([])
        } else {
          setRevealingIds((prev) => [
            ...prev,
            ...notGenericEmails.map((result) => result.id),
          ])
        }
      }
    }
  }

  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)

  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    saveLeads(listId)
  }

  // Calculate costs
  let numberOfEmailsToReveal = 0
  if (selectedIds.length > 0) {
    numberOfEmailsToReveal = results.filter(
      (result) => selectedIds.includes(result.id) && !result.revealed,
    ).length
  }

  const numberOfCreditsNeeded = CostCalculator.calculate(
    "search",
    numberOfEmailsToReveal,
  )
  let neededCredits = numberOfCreditsNeeded - numberOfCreditsLeft
  if (neededCredits < 0) {
    neededCredits = 0
  }
  const revealAllOperationCost = CostCalculator.calculateVisualRepresentation(
    "search",
    numberOfEmailsToReveal,
  )

  const decisionMakersListRef = React.useRef(null)
  const genericListRef = React.useRef(null)

  const decisionMakersListVirtualizer = useVirtualizer({
    count: decisionMakersResults.length,
    getScrollElement: () => decisionMakersListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "decision_makers",
  })
  const genericListVirtualizer = useVirtualizer({
    count: genericResults.length,
    getScrollElement: () => genericListRef.current,
    estimateSize: React.useCallback(() => estimatedSize, []),
    enabled: tabSelected === "generic",
  })

  const [revealingGeneric, setRevealingGeneric] = React.useState(false)
  const genericRevealed = React.useMemo(() => {
    return revealingGeneric || results.some((result) => result.revealed)
  }, [results, revealingGeneric])

  return (
    <>
      <TooltipProvider>
        <header className="ds-results__header">
          <h2
            className="ds-results__title"
            data-domain-search--search-target="resultsTitle"
          >
            <strong>
              {i18n.t("js.search.results", {
                count: numberOfResults || 0,
              })}
            </strong>{" "}
            {i18n.t("js.search.for")} {domain.value}
          </h2>
          <div className="ml-auto d-flex gap-2">
            <Button size="sm" onClick={() => setShowFilters(!showFilters)}>
              <span className="far fa-sliders-simple" aria-hidden="true"></span>
              Filters
              {showFilters ? (
                <span className="far fa-angle-up" aria-hidden="true"></span>
              ) : (
                <span className="far fa-angle-down" aria-hidden="true"></span>
              )}
            </Button>
            <Button
              size="sm"
              onClick={() => setShowFindByName(!showFindByName)}
            >
              <span className="far fa-circle-user" aria-hidden="true"></span>
              Find by Name
              {showFindByName ? (
                <span className="far fa-angle-up" aria-hidden="true"></span>
              ) : (
                <span className="far fa-angle-down" aria-hidden="true"></span>
              )}
            </Button>
          </div>
        </header>

        {showFilters && (
          <Filters
            id="company-emails-filters"
            filters={filters}
            setFilters={(newFilters) => setFilters(newFilters)}
            jobTitles={jobTitles}
            anyFiltersSet={anyFiltersSet}
            clearFilters={() => clearFilters(setFilters)}
            possibleFilters={[
              "department",
              "job_title",
              "already_saved",
              "verification_status",
              "required_field",
              "location",
            ]}
          />
        )}

        {showFindByName && (
          <form
            className="ds-finder"
            data-controller="domain-search--email-finder loading"
            data-domain-search--email-finder-domain-value={domain.value}
            data-loading-text-value=""
            data-domain-search--email-finder-version-value="domain_search"
            data-action="domain-search--email-finder#find loading#show domain-search--email-finder:loaded@window->loading#hide domain-search--email-finder:loaded@window->domain-search--search#updateResultsTitle"
          >
            <div className="ds-finder-form">
              <div className="ds-finder-form-name">
                <svg className="ds-finder-form-name__icon">
                  <use xlinkHref="#icon-email-finder" />
                </svg>
                <input
                  id="full-name-field"
                  className="ds-finder-form-name__field"
                  type="text"
                  placeholder={i18n.t(
                    "js.search.find_by_name.fullname_placeholder",
                  )}
                  data-domain-search--email-finder-target="name"
                  data-focus-element-target="element"
                />
              </div>
              <button
                className="ds-finder-form__submit"
                type="button"
                aria-label={i18n.t("js.search.find_by_name.find_this_person")}
                data-loading-target="element"
                data-action="domain-search--email-finder#find loading#show"
              >
                <span className="far fa-search"></span>
              </button>
            </div>
          </form>
        )}

        <turbo-frame id="domain_search_email_finder_email" />

        {/* Bulk Actions Toolbar - matches original ds-bulk-actions structure */}
        <BulkActionsToolbar
          className={selectedIds.length > 0 ? "is-visible" : ""}
          selectedIds={selectedIds}
          numberOfResults={numberOfResults}
          currentCredits={numberOfCreditsLeft}
          currentTab={tabSelected}
          numberOfResultsForCurrentTab={
            tabSelected === "all_people"
              ? allPeopleResults.length
              : tabSelected === "decision_makers"
                ? decisionMakersResults.length
                : genericResults.length
          }
          removeSelection={() => setSelectedIds([])}
          selectAll={() =>
            setSelectedIds(filteredResults.map((result) => result.id))
          }
          selectCurrentTab={() => {
            const resultsToSelect =
              tabSelected === "all_people"
                ? allPeopleResults
                : tabSelected === "decision_makers"
                  ? decisionMakersResults
                  : genericResults

            setSelectedIds((prev) => [
              ...new Set([
                ...prev,
                ...resultsToSelect.map((result) => result.id),
              ]),
            ])
          }}
          saveLeads={saveLeads}
          handleLeadManagerSave={handleLeadManagerSave}
          leadsLists={leadsLists}
          reavealingIds={reavealingIds}
          revealAllOperationCost={revealAllOperationCost}
          exportEmails={() => exportEmails(selectedIds)}
        />

        <div className="ds-results__body mb-4" id="domain_search_emails">
          {/* Tab Headers */}
          <div className="my-4">
            <PillTabs defaultValue="all_people">
              <PillTabsList size="md" fullWidth className="my-4">
                <PillTabsTrigger
                  value="all_people"
                  onClick={() => setTabSelected("all_people")}
                >
                  {i18n.t("js.search.tabs.all_people")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(
                      allPeopleResults.length,
                    )}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
                <PillTabsTrigger
                  value="decision_makers"
                  onClick={() => setTabSelected("decision_makers")}
                >
                  {i18n.t("js.search.tabs.decision_makers")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(
                      decisionMakersResults.length,
                    )}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
                <PillTabsTrigger
                  value="generic"
                  onClick={() => setTabSelected("generic")}
                >
                  {i18n.t("js.search.tabs.generic")}
                  <PillTabsTriggerHint>
                    ·{" "}
                    {CurrencyHelper.numberWithDelimiter(genericResults.length)}
                  </PillTabsTriggerHint>
                </PillTabsTrigger>
              </PillTabsList>
              <PillTabsContent value="all_people">
                {Object.entries(departmentsList).map(([key, value]) => (
                  <DepartmentSection
                    key={key}
                    department={value}
                    title={value}
                    hideCompanyDetails={true}
                    results={
                      resultsByDepartment.find(
                        ([department, departmentResults]) => department === key,
                      )?.[1] || []
                    }
                    selectedIds={selectedIds}
                    onSelect={handleSelect}
                    handleReveal={handleReveal}
                    handleLeadsListSave={handleLeadsListSave}
                    handleCampaignsSave={handleCampaignsSave}
                    leadsLists={leadsLists}
                    campaigns={campaigns}
                    domain={domain}
                    defaultExpanded={
                      Object.entries(departmentsList)[0][0] === key
                    }
                  />
                ))}
                <DepartmentSection
                  key="unknown"
                  department="Department unknown"
                  title="Department unknown"
                  hideCompanyDetails={true}
                  results={
                    resultsByDepartment.find(
                      ([department, departmentResults]) =>
                        department === "null",
                    )?.[1] || []
                  }
                  selectedIds={selectedIds}
                  onSelect={handleSelect}
                  handleReveal={handleReveal}
                  handleLeadsListSave={handleLeadsListSave}
                  handleCampaignsSave={handleCampaignsSave}
                  leadsLists={leadsLists}
                  campaigns={campaigns}
                  domain={domain}
                  defaultExpanded={resultsByDepartment[0]?.[0] === "null"}
                />

                {isLoading && <LoadingSpinner />}
                {allPeopleResults.length === 0 &&
                  !isLoading &&
                  (anyFiltersSet ? (
                    <EmptyState clearFilters={clearFilters} />
                  ) : (
                    <EmptyState />
                  ))}
              </PillTabsContent>
              <PillTabsContent
                value="decision_makers"
                ref={decisionMakersListRef}
                style={{ height: "600px", overflow: "auto" }}
              >
                <div
                  style={
                    decisionMakersResults.length > 0
                      ? {
                          height: `${decisionMakersListVirtualizer.getTotalSize()}px`,
                          position: "relative",
                        }
                      : undefined
                  }
                >
                  {decisionMakersListVirtualizer
                    .getVirtualItems()
                    .map((virtualItem) => (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={decisionMakersListVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <Result
                          result={decisionMakersResults[virtualItem.index]}
                          domain={domain}
                          selected={selectedIds.includes(
                            decisionMakersResults[virtualItem.index]?.id,
                          )}
                          hideCompanyDetails={true}
                          leadsLists={leadsLists}
                          campaigns={campaigns}
                          onSelect={handleSelect}
                          handleReveal={handleReveal}
                          handleLeadsListSave={handleLeadsListSave}
                          handleCampaignsSave={handleCampaignsSave}
                        />
                      </div>
                    ))}
                  {isLoading && <LoadingSpinner />}
                  {decisionMakersResults.length === 0 &&
                    !isLoading &&
                    (anyFiltersSet ? (
                      <EmptyState clearFilters={clearFilters} />
                    ) : (
                      <EmptyState />
                    ))}
                </div>
              </PillTabsContent>

              <PillTabsContent
                value="generic"
                ref={genericListRef}
                style={{ height: "600px", overflow: "auto" }}
              >
                {!genericRevealed && (
                  <Alert
                    variant="info"
                    size="small"
                    className="mb-2 align-items-center"
                  >
                    <AlertIcon>
                      <span className="h-alert__icon far fa-info-circle"></span>
                    </AlertIcon>
                    <AlertContent className="text-xs">
                      {i18n.t("js.search.generic_email_badge.description", {
                        pretty_count:
                          CurrencyHelper.numberWithDelimiter(
                            genericEmailsCount,
                          ),
                      })}
                    </AlertContent>
                    <Button size="sm" onClick={() => handleReveal("generic")}>
                      {i18n.t("js.search.generic_email_badge.cta")}
                      <ButtonSuffix>
                        <span className="cost-indicator">
                          <Icon
                            icon="coin"
                            size="xs"
                            className="cost-indicator__icon"
                          />
                          1
                        </span>
                      </ButtonSuffix>
                    </Button>
                  </Alert>
                )}
                <div
                  style={
                    genericResults.length > 0
                      ? {
                          height: `${genericListVirtualizer.getTotalSize()}px`,
                          position: "relative",
                        }
                      : undefined
                  }
                >
                  {genericListVirtualizer
                    .getVirtualItems()
                    .map((virtualItem) => (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={genericListVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <Result
                          result={genericResults[virtualItem.index]}
                          domain={domain}
                          hideCompanyDetails={true}
                          selected={selectedIds.includes(
                            genericResults[virtualItem.index]?.id,
                          )}
                          leadsLists={leadsLists}
                          campaigns={campaigns}
                          onSelect={handleSelect}
                          handleReveal={handleReveal}
                          handleLeadsListSave={handleLeadsListSave}
                          handleCampaignsSave={handleCampaignsSave}
                        />
                      </div>
                    ))}
                  {isLoading && <LoadingSpinner />}
                  {genericResults.length === 0 &&
                    !isLoading &&
                    (anyFiltersSet ? (
                      <EmptyState clearFilters={clearFilters} />
                    ) : (
                      <EmptyState />
                    ))}
                </div>
              </PillTabsContent>
            </PillTabs>
          </div>

          {/* Footer with action buttons */}
          <div
            className={`snackbar ${revealedIds.length > 0 ? "is-visible" : ""}`}
          >
            <Icon
              icon="check-circle"
              variant="solid"
              size="sm"
              className="text-success"
            />

            <span className="snackbar__text">
              <strong className="fw-medium">
                {i18n.t("js.multi_domain_search.footer.leads", {
                  count: revealedIds.length,
                  pretty_count: CurrencyHelper.numberWithDelimiter(
                    revealedIds.length,
                  ),
                })}
              </strong>
              &nbsp;
              {i18n.t("js.multi_domain_search.footer.saved")}
            </span>
            <a
              href={leads_path({
                created_at: `${firstRevealAt?.toISOString().split("T")[0]}|`,
              })}
              data-turbo-frame="_top"
              className="snackbar__link"
            >
              See in leads
            </a>
          </div>
        </div>
      </TooltipProvider>
    </>
  )
}
